import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { faHome, faBars, faTimes, faArrowRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavItem } from "./NavItem";
import type { NavItemType } from "./NavItem";
import { ViewAsBanner } from "./ViewAsBanner";
import { canViewAsClient } from "@/utils/auth-utils";
import { User } from "@/tracker-types";
import { NotificationBell } from "@/components/ui/NotificationBell/NotificationBell";
import { useNotifications } from "@/hooks/useNotifications/useNotifications";

const ClientSelect = lazy(() => import("@/components/controls/Selects/ClientSelect"));

interface MobileNavProps {
  user: { role?: string } | null;
  clientUser?: User | null;
  viewAsId: number | null;
  mainNavItems: NavItemType[];
  secondaryNavItems: NavItemType[];
  handleViewAsChange: (clientId: number | null) => void;
  handleLogoutClick: () => void;
}

export function MobileNav({
  user,
  clientUser,
  viewAsId,
  mainNavItems,
  secondaryNavItems,
  handleViewAsChange,
  handleLogoutClick,
}: MobileNavProps) {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const { unreadCount } = useNotifications(false);
  const hasUnread = user?.role === "client" && unreadCount > 0;
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const bottomNavRef = useRef<HTMLElement>(null);
  const [bottomNavHeight, setBottomNavHeight] = useState(70);

  // Measure bottom nav height dynamically so the slide-out panel never overlaps it
  useEffect(() => {
    const el = bottomNavRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBottomNavHeight(el.offsetHeight));
    ro.observe(el);
    setBottomNavHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    menuButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMenuOpen && !target.closest(".navigation-container")) {
        closeMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isMenuOpen) {
        closeMenu();
        return;
      }
      // Trap focus within the slide-out panel
      if (event.key === "Tab" && isMenuOpen && menuPanelRef.current) {
        const focusableSelectors = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const focusableElements = Array.from(menuPanelRef.current.querySelectorAll<HTMLElement>(focusableSelectors));
        if (focusableElements.length === 0) return;
        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMenuOpen, closeMenu]);

  // Move focus into panel when it opens
  useEffect(() => {
    if (isMenuOpen && menuPanelRef.current) {
      const focusableSelectors = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const first = menuPanelRef.current.querySelector<HTMLElement>(focusableSelectors);
      first?.focus();
    }
  }, [isMenuOpen]);

  const handleNavClick = () => {
    closeMenu();
  };

  const handleClientSelect = (clientId: number | null) => {
    handleViewAsChange(clientId);
    closeMenu();
  };

  const totalNavItems = 1 + (mainNavItems?.length || 0) + (secondaryNavItems?.length || 0);
  const canFitLogoutInMobileNav = totalNavItems <= 4;

  const menuButtonLabel = hasUnread
    ? `Menu (${unreadCount} unread notification${unreadCount > 1 ? "s" : ""})`
    : "Menu";

  return (
    <div className={`md:hidden navigation-container ${viewAsId ? "pt-9" : ""}`}>
      {viewAsId && <ViewAsBanner clientUser={clientUser} handleExitView={() => handleViewAsChange(null)} isMobile />}

      {/* Fixed Bottom Bar */}
      <nav ref={bottomNavRef} className="fixed bottom-0 left-0 right-0 z-50 border-t border-light-border bg-light-background/95 dark:bg-dark-background/70 backdrop-blur-md dark:border-dark-border safe-area-inset-bottom">
        <div className="flex items-center justify-around px-1 py-1">
          <NavItem to="/" icon={faHome} label="Home" variant="mobile-bar" onClick={handleNavClick} />

          {/* Show main nav items - adjust count based on whether we can fit logout */}
          {mainNavItems?.slice(0, canFitLogoutInMobileNav ? mainNavItems.length : 3).map((item) => (
            <NavItem
              key={item.path}
              to={item.path}
              icon={item.icon}
              label={item.label}
              variant="mobile-bar"
              onClick={handleNavClick}
            />
          ))}

          {/* Show logout button if we can fit it, otherwise show menu button */}
          {canFitLogoutInMobileNav ? (
            <button
              onClick={handleLogoutClick}
              aria-label="Logout"
              className="flex flex-col items-center px-2 py-2 space-y-1 transition-all duration-200 rounded-lg text-light/80 dark:text-dark/70"
            >
              <FontAwesomeIcon icon={faArrowRightFromBracket} className="w-5 h-5" aria-hidden="true" />
              <span className="text-xs font-medium">Logout</span>
            </button>
          ) : (
            <button
              ref={menuButtonRef}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-haspopup="dialog"
              aria-expanded={isMenuOpen}
              aria-label={menuButtonLabel}
              className="relative flex flex-col items-center px-2 py-2 space-y-1 transition-all duration-200 rounded-lg text-light/80 dark:text-dark/70"
            >
              <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} className="w-5 h-5" aria-hidden="true" />
              {hasUnread && !isMenuOpen && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" aria-hidden="true" />
              )}
              <span className="text-xs font-medium" aria-hidden="true">Menu</span>
            </button>
          )}
        </div>
      </nav>

      {/* Slide-out Menu Overlay — always in DOM so CSS transitions fire correctly */}
      <div
        onClick={closeMenu}
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ease-out motion-reduce:transition-none ${isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      >
        <div
          ref={menuPanelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          aria-hidden={!isMenuOpen}
          inert={!isMenuOpen}
          onClick={(e) => e.stopPropagation()}
          style={{ height: `calc(100dvh - ${bottomNavHeight}px)` }}
          className={`fixed top-0 right-0 w-64 transition-transform duration-300 ease-out motion-reduce:transition-none shadow-2xl bg-light-background dark:bg-dark-background/70 ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-6 border-b border-light-border dark:border-dark-border">
              <h2 className="text-lg font-semibold text-light dark:text-dark">Menu</h2>
              <div className="flex items-center gap-1">
                {user?.role === "client" && <NotificationBell />}
                <button
                  onClick={closeMenu}
                  aria-label="Close menu"
                  className="p-2 transition-colors rounded-lg hover:bg-light-muted/60 dark:hover:bg-dark-surface/40"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </div>

            <div className="flex-1 py-4 overflow-y-auto">
              <div className="px-4 space-y-2">
                {/* View As Selection for Mobile */}
                {canViewAsClient(user?.role) && !viewAsId && (
                  <div className="pb-4 mb-4 border-b border-light-border dark:border-dark-border">
                    <p className="mb-2 text-sm font-medium text-light dark:text-dark">View as Client</p>
                    <Suspense fallback={<div className="w-full rounded h-9 bg-light-border/20 animate-pulse" />}>
                      <ClientSelect labelName="" value={viewAsId} onChange={handleClientSelect} />
                    </Suspense>
                  </div>
                )}
                {/* Remaining main nav items */}
                {mainNavItems?.slice(3).map((item) => (
                  <NavItem
                    key={item.path}
                    to={item.path}
                    icon={item.icon}
                    label={item.label}
                    variant="slideout"
                    onClick={handleNavClick}
                  />
                ))}

                {/* Secondary nav items */}
                {secondaryNavItems && secondaryNavItems.length > 0 && (
                  <>
                    <div className="my-4 border-t border-light-border dark:border-dark-border"></div>
                    {secondaryNavItems.map((item) => (
                      <NavItem
                        key={item.path}
                        to={item.path}
                        icon={item.icon}
                        label={item.label}
                        variant="slideout"
                        onClick={handleNavClick}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Show logout in menu when menu button is shown (not all items fit in bottom nav) */}
            {!canFitLogoutInMobileNav && (
              <div className="p-4 border-t border-light-border dark:border-dark-border">
                <button
                  onClick={handleLogoutClick}
                  aria-label="Logout"
                  className="flex items-center w-full px-4 py-3 space-x-3 transition-all duration-200 rounded-lg text-light/90 dark:text-dark/80 hover:bg-light-muted/60 dark:hover:bg-dark-surface/40"
                >
                  <FontAwesomeIcon icon={faArrowRightFromBracket} className="w-5 h-5" aria-hidden="true" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
